import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { alpha } from "../../theme/tokens";
import { useDesignTokens } from "../../context/ThemeContext";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Typography,
  Box,
  IconButton,
  Tooltip,
  Button,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { DEFAULT_TANK_NOZZLES, DEFAULT_TOWER_NOZZLES } from "./budgetFormConfig";

const isRowEmpty = (row) =>
  !row.designation && !row.qty && !row.dn && !row.pn && !row.unitPrice && !row.price;

const NozzleListEditor = ({ value, onChange }) => {
  const { t } = useTranslation();
  const tokens = useDesignTokens();
  const [isEditing, setIsEditing] = useState(false);
  const resolvedValue = value && typeof value === "object" ? value : {};

  const initRows = (defaults) =>
    defaults.map((row) => ({ ...row }));

  const [tankNozzles, setTankNozzles] = useState(
    resolvedValue.tankNozzles || initRows(DEFAULT_TANK_NOZZLES)
  );
  const [towerNozzles, setTowerNozzles] = useState(
    resolvedValue.towerNozzles || initRows(DEFAULT_TOWER_NOZZLES)
  );
  const firstInputRef = useRef(null);
  const hasInitialized = useRef(false);

  const filterEmptyRows = (rows) => rows.filter((r) => !isRowEmpty(r));

  const emitChange = (tank, tower) => {
    onChange("nozzleList", { tankNozzles: tank, towerNozzles: tower });
  };

  useEffect(() => {
    if (!hasInitialized.current) {
      hasInitialized.current = true;
      emitChange(tankNozzles, towerNozzles);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (hasInitialized.current) {
      emitChange(tankNozzles, towerNozzles);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tankNozzles, towerNozzles]);

  useEffect(() => {
    if (isEditing && firstInputRef.current) {
      setTimeout(() => {
        firstInputRef.current.focus();
        firstInputRef.current.select();
      }, 100);
    }
  }, [isEditing]);

  const handleCellChange = (list, setList, rowIndex, field, newValue) => {
    const updated = [...list];
    updated[rowIndex] = { ...updated[rowIndex], [field]: newValue };
    setList(updated);
  };

  const handleAddRow = (list, setList) => {
    if (list.length > 0 && isRowEmpty(list[list.length - 1])) return;
    const newRow = { designation: "", qty: "", dn: "", pn: "", unitPrice: "", price: "", _isCustom: true };
    setList([...list, newRow]);
  };

  const handleDeleteRow = (list, setList, rowIndex) => {
    setList(list.filter((_, i) => i !== rowIndex));
  };

  const handleToggleEdit = () => {
    if (isEditing) {
      const cleanTank = filterEmptyRows(tankNozzles);
      const cleanTower = filterEmptyRows(towerNozzles);
      setTankNozzles(cleanTank);
      setTowerNozzles(cleanTower);
    }
    setIsEditing(!isEditing);
  };

  const renderTable = (title, rows, setRows, isFirstTable = false) => {
    return (
      <Box sx={{ flex: 1, minWidth: 0, maxWidth: "50%" }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 700,
              color: "primary.main",
              fontSize: "0.9rem",
            }}
          >
            {title}
          </Typography>
          {isEditing && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => handleAddRow(rows, setRows)}
              sx={{
                borderColor: "primary.main",
                color: "primary.main",
                textTransform: "none",
                fontSize: "0.78rem",
                minWidth: 0,
                "&:hover": { borderColor: "primary.dark", bgcolor: alpha.primary04 },
              }}
            >
              {t("budgetForm.nozzleEditor.add")}
            </Button>
          )}
        </Box>
        <TableContainer
          component={Paper}
          variant="outlined"
          sx={{
            borderRadius: 2,
            boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
          }}
        >
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: alpha.primary30 }}>
                <TableCell sx={{ fontWeight: 700, color: "var(--text-primary)", py: 0.75, width: "24%" }}>{t("budgetForm.nozzleEditor.designation")}</TableCell>
                <TableCell sx={{ fontWeight: 700, color: "var(--text-primary)", py: 0.75, width: "10%" }}>{t("budgetForm.nozzleEditor.qty")}</TableCell>
                <TableCell sx={{ fontWeight: 700, color: "var(--text-primary)", py: 0.75, width: "14%" }}>{t("budgetForm.nozzleEditor.dn")}</TableCell>
                <TableCell sx={{ fontWeight: 700, color: "var(--text-primary)", py: 0.75, width: "14%" }}>{t("budgetForm.nozzleEditor.pn")}</TableCell>
                <TableCell sx={{ fontWeight: 700, color: "var(--text-primary)", py: 0.75, width: "16%" }}>{t("budgetForm.nozzleEditor.unitPrice")}</TableCell>
                <TableCell sx={{ fontWeight: 700, color: "var(--text-primary)", py: 0.75, width: "14%" }}>{t("budgetForm.nozzleEditor.price")}</TableCell>
                {isEditing && (
                  <TableCell sx={{ fontWeight: 700, color: "var(--text-primary)", py: 0.75, width: "8%" }}></TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row, idx) => (
                <TableRow
                  key={idx}
                  sx={{
                    "&:nth-of-type(odd)": { bgcolor: "rgba(0,0,0,0.02)" },
                  }}
                >
                  <TableCell sx={{ py: 0.4, px: 1.5 }}>
                    {isEditing && row._isCustom ? (
                      <TextField
                        inputRef={isFirstTable && idx === 0 ? firstInputRef : null}
                        variant="standard"
                        value={row.designation}
                        onChange={(e) => handleCellChange(rows, setRows, idx, "designation", e.target.value)}
                        InputProps={{
                          disableUnderline: false,
                          sx: { fontSize: "0.82rem" }
                        }}
                        fullWidth
                      />
                    ) : (
                      <span style={{ fontSize: "0.85rem", color: "var(--text-primary)", fontWeight: 500 }}>
                        {row.designation}
                      </span>
                    )}
                  </TableCell>

                  <TableCell sx={{ py: 0.4, px: 1.5 }}>
                    {isEditing ? (
                      <TextField
                        variant="standard"
                        value={row.qty}
                        onChange={(e) => handleCellChange(rows, setRows, idx, "qty", e.target.value)}
                        InputProps={{
                          disableUnderline: false,
                          sx: { fontSize: "0.82rem" }
                        }}
                        fullWidth
                      />
                    ) : (
                      <span style={{ fontSize: "0.85rem", color: row.qty ? "var(--text-primary)" : "var(--text-secondary)" }}>
                        {row.qty || "—"}
                      </span>
                    )}
                  </TableCell>

                  <TableCell sx={{ py: 0.4, px: 1.5 }}>
                    {isEditing ? (
                      <TextField
                        variant="standard"
                        value={row.dn}
                        onChange={(e) => handleCellChange(rows, setRows, idx, "dn", e.target.value)}
                        InputProps={{
                          disableUnderline: false,
                          sx: { fontSize: "0.82rem" }
                        }}
                        fullWidth
                      />
                    ) : (
                      <span style={{ fontSize: "0.85rem", color: row.dn ? "var(--text-primary)" : "var(--text-secondary)" }}>
                        {row.dn || "—"}
                      </span>
                    )}
                  </TableCell>

                  <TableCell sx={{ py: 0.4, px: 1.5 }}>
                    {isEditing ? (
                      <TextField
                        variant="standard"
                        value={row.pn}
                        onChange={(e) => handleCellChange(rows, setRows, idx, "pn", e.target.value)}
                        InputProps={{
                          disableUnderline: false,
                          sx: { fontSize: "0.82rem" }
                        }}
                        fullWidth
                      />
                    ) : (
                      <span style={{ fontSize: "0.85rem", color: row.pn ? "var(--text-primary)" : "var(--text-secondary)" }}>
                        {row.pn || "—"}
                      </span>
                    )}
                  </TableCell>

                  <TableCell sx={{ py: 0.4, px: 1.5 }}>
                    {isEditing ? (
                      <TextField
                        variant="standard"
                        value={row.unitPrice}
                        onChange={(e) => handleCellChange(rows, setRows, idx, "unitPrice", e.target.value)}
                        InputProps={{
                          disableUnderline: false,
                          sx: { fontSize: "0.82rem" }
                        }}
                        fullWidth
                      />
                    ) : (
                      <span style={{ fontSize: "0.85rem", color: row.unitPrice ? "var(--text-primary)" : "var(--text-secondary)" }}>
                        {row.unitPrice || "—"}
                      </span>
                    )}
                  </TableCell>

                  <TableCell sx={{ py: 0.4, px: 1.5 }}>
                    {isEditing ? (
                      <TextField
                        variant="standard"
                        value={row.price}
                        onChange={(e) => handleCellChange(rows, setRows, idx, "price", e.target.value)}
                        InputProps={{
                          disableUnderline: false,
                          sx: { fontSize: "0.82rem" }
                        }}
                        fullWidth
                      />
                    ) : (
                      <span style={{ fontSize: "0.85rem", color: row.price ? "var(--text-primary)" : "var(--text-secondary)" }}>
                        {row.price || "—"}
                      </span>
                    )}
                  </TableCell>

                  {isEditing && (
                    <TableCell sx={{ py: 0.4, px: 0.5 }}>
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteRow(rows, setRows, idx)}
                        sx={{ color: "error.dark", "&:hover": { bgcolor: alpha.error08 } }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%" }}>
      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1.5 }}>
        <Tooltip title={isEditing ? t("budgetForm.nozzleEditor.save") : t("budgetForm.nozzleEditor.edit")}>
          <IconButton
            onClick={handleToggleEdit}
            sx={{
              bgcolor: isEditing ? "primary.main" : "transparent",
              color: isEditing ? "#fff" : "primary.main",
              border: `1px solid ${tokens.primary}`,
              width: 36,
              height: 36,
              "&:hover": {
                bgcolor: isEditing ? "primary.dark" : alpha.primary08,
              },
            }}
          >
            {isEditing ? <SaveIcon fontSize="small" /> : <EditIcon fontSize="small" />}
          </IconButton>
        </Tooltip>
      </Box>

      <Box sx={{ display: "flex", gap: 3, width: "100%" }}>
        {renderTable(t("budgetForm.nozzleEditor.tankTable"), tankNozzles, setTankNozzles, true)}
        {renderTable(t("budgetForm.nozzleEditor.towerTable"), towerNozzles, setTowerNozzles)}
      </Box>
    </Box>
  );
};

export default NozzleListEditor;
